/**
 * Mission cost / feasibility chips — a row of five compact readouts above
 * the action buttons in the Editor section of the mission planner panel.
 *
 *   Time      — total simulated flight duration (reuses liveViewSimulator's
 *               leg-plan math so the chip matches the live-view HUD timer).
 *   Distance  — sum of haversine over user legs + home legs (when dock
 *               located).
 *   Battery   — rough estimate based on a 45 min battery life (Mavic 3T
 *               nominal). NOT the simulator's 100→70% mission-arc model —
 *               that one always lands at 70% regardless of mission length,
 *               which would make the chip useless. A real flight-feasibility
 *               readout has to be duration-driven.
 *   Photos    — predicted capture EVENTS (one per per-waypoint photo action
 *               or per interval-photo fire). Matches the simulator's HUD
 *               counter so users get a consistent number across screens.
 *   Status    — pass/fail rollup of validateMission(mission, dock). When
 *               there are errors the chip turns red, the count is shown,
 *               and the tooltip lists every error message in order.
 *
 * Status is the SAME validation that gates Send / Export in the panel — the
 * chip just surfaces it visually so users see "why can't I send?" at a
 * glance without scrolling through every leg card.
 */

import type { DemoMission, DemoDockStation } from './types';
import type { ValidationError } from '../../utils/missionValidation';
import { getTotalDurationSec } from '../../utils/liveViewSimulator';
import { haversineM } from '../../utils/missionValidation';
import { resolveEffectiveLegParams } from '../../utils/missionValidation';

interface Props {
  mission: DemoMission;
  dock: DemoDockStation | null;
  validationErrors: ValidationError[];
}

// Mavic 3T nominal battery life ≈ 45 min — gives ~2.22 % drain per minute.
// This is a single conservative constant rather than a per-mode model
// because the demo doesn't track per-leg power draw; for a planning chip,
// duration is the dominant variable.
const BATTERY_MIN_LIFE = 45;

function fmtTime(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
}

function fmtDistance(m: number): string {
  if (!Number.isFinite(m) || m <= 0) return '—';
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;
}

function totalDistanceM(
  mission: DemoMission,
  dock: DemoDockStation | null,
): number {
  const wps = mission.waypoints;
  if (wps.length === 0) return 0;
  let total = 0;
  for (let i = 0; i < wps.length - 1; i++) {
    total += haversineM(
      [wps[i].lng, wps[i].lat],
      [wps[i + 1].lng, wps[i + 1].lat],
    );
  }
  // Home legs included only when the dock has been located. (0,0) means
  // uninitialized — drone won't actually launch / return to null island.
  if (dock && (dock.lng !== 0 || dock.lat !== 0)) {
    total += haversineM([dock.lng, dock.lat], [wps[0].lng, wps[0].lat]);
    total += haversineM(
      [wps[wps.length - 1].lng, wps[wps.length - 1].lat],
      [dock.lng, dock.lat],
    );
  }
  return total;
}

function countPredictedPhotos(mission: DemoMission): number {
  const wps = mission.waypoints;
  let count = 0;
  for (let i = 0; i < wps.length; i++) {
    const wp = wps[i];
    // Per-waypoint photo action contributes one capture event (the lens
    // array may be multi for AB capture, but it counts as a single event —
    // mirroring the simulator's HUD counter).
    if (wp.action === 'photo' && (wp.photoPayload?.length ?? 0) > 0) {
      count++;
    }
    // Distance-interval shooting on the leg LEAVING this waypoint.
    if (i < wps.length - 1) {
      const lp = resolveEffectiveLegParams(wps, i);
      if (
        lp.intervalPhoto?.enabled &&
        lp.intervalPhoto.lenses.length > 0 &&
        lp.intervalPhoto.distanceM > 0
      ) {
        const distM = haversineM(
          [wps[i].lng, wps[i].lat],
          [wps[i + 1].lng, wps[i + 1].lat],
        );
        // floor(legDist / step) fires per leg — matches the simulator's
        // photoCaptureCount calculation (lines ~580-589).
        const fires = Math.floor((distM - 1e-9) / lp.intervalPhoto.distanceM);
        if (fires > 0) count += fires;
      }
    }
  }
  return count;
}

export default function MissionCostChips({ mission, dock, validationErrors }: Props) {
  // Time uses the simulator's full leg-plan math so the chip matches the
  // live-view HUD. dock is required by getTotalDurationSec (it needs the
  // home anchor for outbound/return travel legs); when unbound, we show "—".
  const timeSec = dock ? getTotalDurationSec(mission, dock) : 0;
  const distM = totalDistanceM(mission, dock);
  const photos = countPredictedPhotos(mission);
  const drainPct = timeSec > 0 ? Math.min(100, (timeSec / 60) * (100 / BATTERY_MIN_LIFE)) : 0;
  const remainingPct = Math.max(0, 100 - drainPct);

  const hasErrors = validationErrors.length > 0;
  const statusLabel = hasErrors
    ? `✗ ${validationErrors.length} issue${validationErrors.length > 1 ? 's' : ''}`
    : '✓ Ready';
  const statusTitle = hasErrors
    ? validationErrors.map((e) => `• ${e.message}`).join('\n')
    : 'All checks pass — ready to send / export';

  // Battery chip turns red when the mission would drain below 30 % remaining
  // (the standard DJI return-to-home threshold). Operators want to know if
  // the planned mission is on the edge of the safe envelope.
  const lowBattery = remainingPct < 30;

  return (
    <div className="dock-demo-cost-chips">
      <div className="dock-demo-chip" title="Total simulated flight time (matches Live view's timer)">
        Time <strong>{fmtTime(timeSec)}</strong>
      </div>
      <div className="dock-demo-chip" title="Sum of leg distances including dock home → first waypoint and last waypoint → dock home">
        Distance <strong>{fmtDistance(distM)}</strong>
      </div>
      <div
        className={`dock-demo-chip${lowBattery ? ' dock-demo-chip--warn' : ''}`}
        title={`Estimated battery remaining at landing — based on a ${BATTERY_MIN_LIFE} min nominal Mavic 3T battery life. Drain: ${drainPct.toFixed(0)} %.`}
      >
        Battery <strong>{Math.round(remainingPct)}%</strong>
      </div>
      <div className="dock-demo-chip" title="Predicted capture events (per-waypoint photo actions + distance-interval fires)">
        Photos <strong>{photos}</strong>
      </div>
      <div
        className={`dock-demo-chip${hasErrors ? ' dock-demo-chip--error' : ' dock-demo-chip--ok'}`}
        title={statusTitle}
        style={{ whiteSpace: 'pre-line' }}
      >
        Status <strong>{statusLabel}</strong>
      </div>
    </div>
  );
}
